import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import { Notification } from 'ksnm-common-ui/lib/Notification';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { controlsStore } from '@stores/dictionaryStores/controlsStore/controlsStore';
import { controlVersionEditStore } from '@stores/dictionaryStores/controlVersionsStore/controlVersionEditStore';
import { controlVersionsStore } from '@stores/dictionaryStores/controlVersionsStore/controlVersionsStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { FormWithActions } from '../../FormWithActions';
import { Form } from './Form';
import { StepTable } from './StepTable';
import { validate } from './validateSchema';

import styles from './ControlVersionEdit.module.scss';

export const ControlVersionEdit = observer(() => {
  const { ctrlVersCode } = useParams<{ ctrlVersCode: string }>();
  const { setNew, fetch, setEditMode, controlVersion, save, isNew, isEditMode } = controlVersionEditStore;
  const { selectNode } = controlVersionsStore;
  const { controls } = controlsStore;

  const { state } = useLocation();
  const { push } = useHistory();

  const permission = usePermission('CmControlVersion', controlVersionEditStore);

  useEffect(() => {
    fetchReferences(['roundTypes']);
  }, []);

  // useEffect(() => {
  //   if (controlVersion?.isEditable === false) {
  //     controlVersionEditStore?.setHiddenEdit(true);
  //     controlVersionEditStore?.setHiddenDelete(true);
  //     controlVersionEditStore?.setHiddenClean(true);
  //     controlVersionEditStore?.setHiddenSave(true);
  //
  //     Notification({
  //       type: 'warning',
  //       message: '"С участием данной версии контроля существуют результаты выполнения КП. Изменение невозможно.',
  //       key: 'controlVersionWarning',
  //     });
  //   }
  //
  //   return () => Notification.close('controlVersionWarning');
  // }, [controlVersion?.isEditable]);

  useEffect(() => {
    if (ctrlVersCode !== NEW_ENTITY_ID) {
      fetch(ctrlVersCode);
    } else {
      setNew(true);
    }
    return () => {
      setNew(false);
      setEditMode(false);
    };
  }, [fetch, setNew, setEditMode, ctrlVersCode]);

  const initialValues = useMemo(() => {
    if (state && state.repGroupCode) {
      return { repGroupCode: state.repGroupCode, ctrlCode: state?.ctrlCode };
    }

    return controlVersion
      ? {
          ...controlVersion,
          versionNum: controlVersion.versionNum && String(controlVersion.versionNum).padStart(3, '0'),
          repGroupCode: controls.find((item) => item.ctrlCode === controlVersion.ctrlCode)?.repGroupCode,
        }
      : { ctrlCode: selectNode?.ctrlCode, repGroupCode: selectNode?.repGroupCode };
  }, [controlVersion, controls, selectNode?.ctrlCode, selectNode?.repGroupCode, state]);

  const handleSubmit = useCallback(
    (values) => {
      if ((isNew && !permission.CREATE) || (!isNew && !permission.UPDATE)) {
        Notification({
          message: `У вас нет прав для выполнения операции ${isNew ? 'создания' : 'обновления'} формы`,
        });
        return {};
      }

      const validation = validate(values);

      if (validation) {
        return validation;
      }

      (async () => {
        const value = await save({
          ...values,
          dateFrom: values.dateFrom ? dayjs(new Date(values.dateFrom)).format('YYYY-MM-DDT00:00:00') : null,
          dateTo: values.dateTo ? dayjs(new Date(values.dateTo)).format('YYYY-MM-DDT23:59:59') : null,
          versionNum: +values.versionNum,
          steps: values?.steps?.map((item) => ({
            ctrlVersCode: values.ctrlVersCode,
            stepNum: item.stepNum,
            stepCodeExt: item.stepCodeExt,
            stepType: item.stepType,
            stepName: item.stepName,
            stepDesc: item.stepDesc,
            textSuccess: item.textSuccess,
            textFailLt: item.textFailLt,
            textFailGt: item.textFailGt,
            textFailEq: item.textFailEq,
            codeFns: item.codeFns,
          })),
          stepSettings: values?.steps?.map((item) => ({
            ctrlVersCode: values.ctrlVersCode,
            stepNum: item.stepNum,
            premise: item.premise,
            formula: item.formula,
            ctrlType: item.ctrlType,
            ctrlAnalytics: '',
            ctrlAnalyticsDet: '',
            delta: item.delta ? +item.delta : undefined,
            roundingType: item.roundingType,
            decimalQty: item.decimalQty ? +item.decimalQty : undefined,
          })),
        });

        if (isNew && value?.ctrlVersCode) {
          push(`${RoutePath.DICTIONARY_CONTROL_VERSIONS}/${value.ctrlVersCode}`);
        }
      })();

      return undefined;
    },
    [isNew, permission.CREATE, permission.UPDATE, push, save],
  );

  const goToControls = useCallback(() => push(RoutePath.DICTIONARY_CONTROL_VERSIONS), [push]);

  return (
    <DesignLayout title="Справочник: Версии контролей" scrollEnable requiredHelp={isEditMode}>
      <FormWrapper
        initialValues={initialValues}
        className={styles.form}
        onSubmit={handleSubmit}
        layout="vertical"
        compact
      >
        <FormWithActions store={controlVersionEditStore} goBackLink={goToControls}>
          <Form />
          <StepTable />
        </FormWithActions>
      </FormWrapper>
    </DesignLayout>
  );
});
